import { useCallback, useState } from "react";
import { Dropdown, Menu, Pagination, Table, Tooltip, Typography } from "antd";
import { getUserId, useIsAdmin } from "../../utils/utilFunctions";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import { ErrorCodes } from "../../Api";
import {
	getAllCompanyUsers,
	deleteCompanyUser,
	getCompanyUserById,
	toggleCompanyUserNotifications,
	toggleAdminRole,
	activateAccountForContactUser,
	getAllCompanyUsersV2,
	deleteCompanyUserAdmin,
	makeRepresentativeAdmin,
	revokeMentorRights,
} from "../../Requests/company-users-requests";
import { StatusCodes } from "http-status-codes";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../Containers/ConfirmationModal";
import { makeRepresentative } from "../../Requests/company-users-requests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
	getCompanyUser,
	getCompanyUsers,
} from "../../utils/reactQueriesConstants";
import { getErrorFromResponse } from "../../utils/responseUtils";
import { useHistory } from "react-router-dom";
import { AddUpdateCompanyUserModalForm } from "../Forms";
import useQueryFilters from "../../Hooks/useQueryFilters";
import Filters from "../Filters";
import { MenuProps } from "antd/lib";
import { ItemType } from "antd/es/menu/hooks/useItems";
import { on } from "events";
import { UpdateMentorInfoModalForm } from "../Forms/UpdateMentorInfoModalForm/UpdateMentorInfoModalForm";

enum ActionKeyEnum {
	ActivateAccount = "activateAccount",
	Edit = "edit",
	MarkRepresentative = "markRepresentative",
	Delete = "delete",
	ToggleNotifications = "toggleNotifications",
	ToggleAdminRole = "toggleAdminRole",
	ToggleMentorRole = "toggleMentorRole",
}

type CompanyUserTableProps = {
	companyId: string;
};

const CompanyUserTable = ({ companyId }: CompanyUserTableProps) => {
	const { t } = useTranslation();
	const [
		changeRepresentativeModalVisibility,
		setChangeRepresentativeModalVisibility,
	] = useState(false);
	const [removeUserModalVisibility, setRemoveUserModalVisibility] =
		useState(false);
	const [activateAccountModalVisibility, setActivateAccountModalVisibility] =
		useState(false);
	const [mentorInfoModalVisibility, setMentorInfoModalVisibility] =
		useState(false);
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [selectedRecord, setSelectedRecord] = useState<string>("");
	const [spinning, setSpinning] = useState(false);
	const history = useHistory();
	const query = useQueryFilters({});
	const isAdmin = useIsAdmin();

	let locale = {
		emptyText: t("tableText.noCompanyUsers"),
	};

	const queryClient = useQueryClient();
	const invalidateCompanyUserQuery = async () => {
		await queryClient.invalidateQueries(getCompanyUser);
	};
	const invalidateUsersQuery = async () => {
		await queryClient.invalidateQueries(getCompanyUsers);
	};

	const handleDelete = async (id: string) => {
		setSpinning(true);

		if (!isAdmin) {
			deleteCompanyUser(id)
				.then(() => {
					invalidateUsersQuery();

					openNotification(
						t("account.userDeleted"),
						t("account.userDeletedMessage"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((ex: any) => {
					if (ex.status && ex.status === StatusCodes.NOT_FOUND) {
						openNotification(
							t("account.conflict"),
							t("account.notFoundUser"),
							NOTIFICATION_TYPES.ERROR
						);
					} else if (ex.status && ex.status === StatusCodes.FORBIDDEN) {
						openNotification(
							t("account.error"),
							t("account.notAllowed"),
							NOTIFICATION_TYPES.ERROR
						);
					} else {
						openNotification(
							t("account.error"),
							t("account.unknownError"),
							NOTIFICATION_TYPES.ERROR
						);
					}
				})
				.finally(() => {
					setSpinning(false);
					setRemoveUserModalVisibility(false);
				});
		} else {
			deleteCompanyUserAdmin(id, companyId)
				.then(() => {
					invalidateUsersQuery();

					openNotification(
						t("account.userDeleted"),
						t("account.userDeletedMessage"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((ex: any) => {
					if (ex.status && ex.status === StatusCodes.NOT_FOUND) {
						openNotification(
							t("account.conflict"),
							t("account.notFoundUser"),
							NOTIFICATION_TYPES.ERROR
						);
					} else if (ex.status && ex.status === StatusCodes.FORBIDDEN) {
						openNotification(
							t("account.error"),
							t("account.notAllowed"),
							NOTIFICATION_TYPES.ERROR
						);
					} else {
						openNotification(
							t("account.error"),
							t("account.unknownError"),
							NOTIFICATION_TYPES.ERROR
						);
					}
				})
				.finally(() => {
					setSpinning(false);
					setRemoveUserModalVisibility(false);
				});
		}
	};

	const handleChangeRepresentative = async (id: string) => {
		setSpinning(true);

		if (!isAdmin) {
			makeRepresentative(id)
				.then(() => {
					invalidateCompanyUserQuery();
					invalidateUsersQuery();

					openNotification(
						t("account.representativeChanged"),
						t("account.representativeChangedMessage"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((ex: any) => {
					if (ex.status && ex.status === StatusCodes.FORBIDDEN) {
						openNotification(
							t("account.error"),
							t("account.notAllowed"),
							NOTIFICATION_TYPES.ERROR
						);
					} else {
						openNotification(
							t("account.error"),
							t("account.unknownError"),
							NOTIFICATION_TYPES.ERROR
						);
					}
				})
				.finally(() => {
					setSpinning(false);
					setChangeRepresentativeModalVisibility(false);
				});
		} else {
			makeRepresentativeAdmin(id, companyId)
				.then(() => {
					invalidateCompanyUserQuery();
					invalidateUsersQuery();

					openNotification(
						t("account.representativeChanged"),
						t("account.representativeChangedMessage"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((ex: any) => {
					if (ex.status && ex.status === StatusCodes.FORBIDDEN) {
						openNotification(
							t("account.error"),
							t("account.notAllowed"),
							NOTIFICATION_TYPES.ERROR
						);
					} else {
						openNotification(
							t("account.error"),
							t("account.unknownError"),
							NOTIFICATION_TYPES.ERROR
						);
					}
				})
				.finally(() => {
					setSpinning(false);
					setChangeRepresentativeModalVisibility(false);
				});
		}
	};

	const { data: companyUser } = useQuery([getCompanyUser, isAdmin], () => {
		return !isAdmin
			? getCompanyUserById(getUserId()).catch(() =>
					openNotification(
						t("account.error"),
						t("usersText.errorTexts.failedUserGet"),
						NOTIFICATION_TYPES.ERROR
					)
			  )
			: null;
	});

	const { data: users, isLoading } = useQuery(
		[getCompanyUsers, query.filters],
		async () => {
			if (isAdmin && !query.filters && !companyId) {
				return null;
			}
			return await getAllCompanyUsersV2(
				isAdmin ? { ...query.filters, companyId } : query.filters
			).catch(() =>
				openNotification(
					t("account.error"),
					t("usersText.errorTexts.failedUserGet"),
					NOTIFICATION_TYPES.ERROR
				)
			);
		}
	);

	const getActions = (record: any): ItemType[] | undefined => {
		let actions: MenuProps["items"] = [];

		//Activate Account
		if (
			(companyUser?.isRepresentative ||
				(companyUser?.isAdmin && !record.isRepresentative)) &&
			companyUser.id !== record.id &&
			record?.isContact === true
		) {
			actions.push({
				key: ActionKeyEnum.ActivateAccount,
				label: (
					<Tooltip title={t("account.activateAccount")}>
						<span style={{ display: "flex", alignItems: "center" }}>
							<FontAwesomeIcon
								icon={solid("plus")}
								style={{ marginRight: 8 }}
							/>
							{t("account.activateAccount")}
						</span>
					</Tooltip>
				),
			});
		}
		//Edit
		if (companyUser?.isRepresentative || companyUser?.isAdmin || isAdmin) {
			actions.push({
				key: ActionKeyEnum.Edit,
				label: (
					<AddUpdateCompanyUserModalForm
						type={"edit"}
						userId={record.id}
						isAdmin={isAdmin}
					>
						<span style={{ display: "flex", alignItems: "center" }}>
							<FontAwesomeIcon
								icon={solid("user-pen")}
								style={{ marginRight: 8 }}
							/>
							{t("account.editUser")}
						</span>
					</AddUpdateCompanyUserModalForm>
				),
			});
		}

		// Mark Representative
		if (
			(companyUser?.isRepresentative || isAdmin) &&
			!record?.isRepresentative &&
			record?.isContact !== true
		) {
			actions.push({
				key: ActionKeyEnum.MarkRepresentative,
				label: (
					<Tooltip title={"Apare pe documente si trebuie sa le semneze"}>
						<span style={{ display: "flex", alignItems: "center" }}>
							<FontAwesomeIcon
								icon={solid("house-user")}
								style={{ marginRight: 8 }}
							/>
							{t("account.changeRepresentative")}
						</span>
					</Tooltip>
				),
			});
		}

		//Notifications Toggle
		if (record?.isContact !== true) {
			actions.push({
				key: ActionKeyEnum.ToggleNotifications,
				label: (
					<span style={{ display: "flex", alignItems: "center" }}>
						<FontAwesomeIcon
							icon={
								record.sendNotifications ? solid("bell-slash") : solid("bell")
							}
							style={{ marginRight: 8 }}
						/>
						{record.sendNotifications
							? t("account.deactivateNotifications")
							: t("account.activateNotifications")}
					</span>
				),
			});
		}

		//Admin Role Toggle
		if (record?.isContact !== true) {
			actions.push({
				key: ActionKeyEnum.ToggleAdminRole,
				label: (
					<span style={{ display: "flex", alignItems: "center" }}>
						<FontAwesomeIcon
							icon={record.isAdmin ? solid("user-slash") : solid("user-cog")}
							style={{ marginRight: 8 }}
						/>
						{record.isAdmin
							? t("account.revokeAdminRights")
							: t("account.grantAdminRights")}
					</span>
				),
			});
		}

		//Mentor Role Toggle
		if (record?.isContact !== true) {
			actions.push({
				key: ActionKeyEnum.ToggleMentorRole,
				label: (
					<span style={{ display: "flex", alignItems: "center" }}>
						<FontAwesomeIcon
							icon={
								record.isMentor
									? solid("user-times")
									: solid("chalkboard-teacher")
							}
							style={{ marginRight: 8 }}
						/>
						{record.isMentor
							? t("account.revokeMentorRights")
							: t("account.grantMentorRights")}
					</span>
				),
			});
		}

		// Delete
		if (
			(companyUser?.isRepresentative ||
				isAdmin ||
				(companyUser?.isAdmin && !record.isRepresentative)) &&
			companyUser?.id !== record.id
		) {
			actions.push({
				label: t("account.deleteUser"),
				key: ActionKeyEnum.Delete,
				icon: (
					<FontAwesomeIcon icon={solid("trash")} style={{ fontSize: "15px" }} />
				),
				title: t("account.deleteUser"),
			});
		}
		return actions;
	};

	const onMenuClick = (e: any, record: any) => {
		switch (e.key) {
			case ActionKeyEnum.Edit:
				break;
			case ActionKeyEnum.ActivateAccount:
				setSelectedUserId(record.id);
				setSelectedRecord(record.name);
				setActivateAccountModalVisibility(true);
				break;
			case ActionKeyEnum.MarkRepresentative:
				setSelectedUserId(record.id);
				setSelectedRecord(record.name);
				setChangeRepresentativeModalVisibility(true);
				break;
			case ActionKeyEnum.ToggleNotifications:
				onNotificationToggle(record);
				break;
			case ActionKeyEnum.ToggleAdminRole:
				onAdminRoleToggle(record);
				break;
			case ActionKeyEnum.ToggleMentorRole:
				if (record.isMentor) {
					onMentorRoleRevoke(record);
				} else {
					setSelectedUserId(record.id);
					setSelectedRecord(record.name);
					setMentorInfoModalVisibility(true);
				}
				break;
			case ActionKeyEnum.Delete:
				setSelectedUserId(record.id);
				setSelectedRecord(record.name);
				setRemoveUserModalVisibility(true);
				break;
			default:
				console.warn("Unknown action key:", e.key);
				break;
		}
	};

	const columns = [
		{
			title: t("account.name"),
			dataIndex: "name",
			key: "name",
			fixed: "left" as "left",
		},
		{
			title: t("account.email"),
			dataIndex: "email",
			key: "email",
		},
		{
			title: t("account.phone"),
			dataIndex: "phone",
			key: "phone",
		},
		{
			title: t("account.function"),
			dataIndex: "_function",
			key: "function",
		},
		{
			title: t("account.department"),
			dataIndex: "department",
			key: "department",
		},
		{
			title: t("account.actions"),
			key: "actions",
			dataIndex: "actions",
			fixed: "right" as "right",
			width: 85,
			render: (_text: string | undefined, record: any) => (
				<div>
					<Dropdown
						placement="bottomLeft"
						overlay={
							<Menu
								onClick={(e) => onMenuClick(e, record)}
								items={getActions(record)}
							/>
						}
					>
						<FontAwesomeIcon
							icon={solid("bars")}
							style={{
								cursor: "pointer",
								height: "1rem",
								padding: "6px 0px",
								marginLeft: "13px",
							}}
						/>
					</Dropdown>
				</div>
			),
		},
	];

	const activateAccount = async (id: string) => {
		setSpinning(true);

		activateAccountForContactUser(id)
			.then(() => {
				invalidateCompanyUserQuery();
				invalidateUsersQuery();

				openNotification(
					t("account.activatedAccount"),
					t("account.activatedAccountMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((ex: any) => {
				if (ex.status && ex.status === StatusCodes.FORBIDDEN) {
					openNotification(
						t("account.error"),
						t("account.notAllowed"),
						NOTIFICATION_TYPES.ERROR
					);
				} else {
					openNotification(
						t("account.error"),
						t("account.unknownError"),
						NOTIFICATION_TYPES.ERROR
					);
				}
			})
			.finally(() => {
				setSpinning(false);
				setActivateAccountModalVisibility(false);
			});
	};

	const onNotificationToggle = async (record: any) => {
		setSpinning(true);
		try {
			await toggleCompanyUserNotifications(record.id);
			await invalidateCompanyUserQuery();
			await invalidateUsersQuery();

			if (!record.sendNotifications) {
				openNotification(
					t("account.notificationsEnabled"),
					t("account.notificationsEnabledMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);
			} else {
				openNotification(
					t("account.notificationsDisabled"),
					t("account.notificationsDisabledMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);
			}
		} catch (ex: any) {
			const errorMsg = await getErrorFromResponse(ex);
			openNotification(
				t("account.error"),
				errorMsg?.message || t("account.unknownError"),
				NOTIFICATION_TYPES.ERROR
			);
		} finally {
			setSpinning(false);
		}
	};

	const onAdminRoleToggle = async (record: any) => {
		setSpinning(true);
		try {
			await toggleAdminRole(record.id);
			await invalidateCompanyUserQuery();
			await invalidateUsersQuery();

			if (!record.isAdmin) {
				openNotification(
					t("account.companyAdminNominated"),
					t("account.companyAdminNominatedMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);
			} else {
				openNotification(
					t("account.companyAdminRevoked"),
					t("account.companyAdminRevokedMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);

				if (record.id === getUserId() && !companyUser?.isRepresentative) {
					history.goBack();
				}
			}
		} catch (error: any) {
			const errorMessage = await getErrorFromResponse(error);
			let notificationMessage;

			switch (errorMessage?.code) {
				case ErrorCodes.Forbidden:
					notificationMessage = t("errors.forbidden");
					break;
				case ErrorCodes.UserNotFound:
					notificationMessage = t("account.notFoundUser");
					break;
				default:
					notificationMessage = t("account.unknownError");
					break;
			}

			openNotification(
				t("account.error"),
				notificationMessage,
				NOTIFICATION_TYPES.ERROR
			);
		} finally {
			setSpinning(false);
		}
	};

	const onMentorRoleRevoke = async (record: any) => {
		setSpinning(true);
		try {
			await revokeMentorRights(record.id);
			await invalidateCompanyUserQuery();
			await invalidateUsersQuery();

			openNotification(
				t("account.companyMentorRevoked"),
				t("account.companyMentorRevokedMessage"),
				NOTIFICATION_TYPES.SUCCESS
			);

			if (record.id === getUserId() && !companyUser?.isRepresentative) {
				history.goBack();
			}
		} catch (error: any) {
			const errorMessage = await getErrorFromResponse(error);
			let notificationMessage;

			switch (errorMessage?.code) {
				case ErrorCodes.Forbidden:
					notificationMessage = t("errors.forbidden");
					break;
				case ErrorCodes.UserNotFound:
					notificationMessage = t("account.notFoundUser");
					break;
				default:
					notificationMessage = t("account.unknownError");
					break;
			}

			openNotification(
				t("account.error"),
				notificationMessage,
				NOTIFICATION_TYPES.ERROR
			);
		} finally {
			setSpinning(false);
			if (mentorInfoModalVisibility) {
				setMentorInfoModalVisibility(false);
			}
		}
	};

	const handleMentorInfoModalSuccess = () => {
		invalidateCompanyUserQuery();
		invalidateUsersQuery();
		setMentorInfoModalVisibility(false);
	};

	const onUpdate = useCallback(
		(values: { [key: string]: string[] }) => {
			query.update(values, true);
		},
		[query]
	);

	const onPageUpdate = useCallback(
		(page: number, pageSize: number) => {
			query.update({
				page: page.toString(),
				pageSize: pageSize.toString(),
			});
		},
		[query]
	);

	return (
		<div className="flex flex-col w-full px-4 pb-10">
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					marginBottom: "16px",
				}}
			>
				<AddUpdateCompanyUserModalForm
					type={"add"}
					userId={getUserId()}
					companyId={isAdmin ? companyId : ""}
				/>
			</div>

			<Filters
				filters={[]}
				searchFields={[{ name: "search", label: t("search.byName") }]}
				onUpdate={onUpdate}
				hasSort={false}
			/>

			<div className="flex flex-col gap-10">
				<div className="flex flex-col items-center justify-between md:flex-row">
					<Typography.Title level={3} className="my-0">
						{users?.totalCount} {t("filtering.results")}
					</Typography.Title>
					{!isLoading && (
						<Pagination
							defaultCurrent={users?.page}
							defaultPageSize={users?.pageSize}
							total={users?.totalCount}
							onChange={onPageUpdate}
							locale={{ items_per_page: t("pagination") }}
							showSizeChanger={true}
						/>
					)}
				</div>

				<Table
					locale={locale}
					columns={columns}
					dataSource={users?.data ?? []}
					pagination={false}
					scroll={{ x: "calc(700px + 50%)", y: 420 }}
					rowKey={(record) => record.id!}
				/>

				<ConfirmationModal
					modalText={
						t("account.changeRepresentativeMessage1") +
						selectedRecord +
						t("account.changeRepresentativeMessage2")
					}
					handleFunction={() => handleChangeRepresentative(selectedUserId)}
					modalVisibility={changeRepresentativeModalVisibility}
					title=""
					changeModalVisibility={() =>
						setChangeRepresentativeModalVisibility(false)
					}
					spinning={spinning}
				/>

				<ConfirmationModal
					modalText={
						t("account.deleteUserMessage") +
						selectedRecord +
						"? " +
						t("account.deleteUserMessage2")
					}
					handleFunction={() => handleDelete(selectedUserId)}
					modalVisibility={removeUserModalVisibility}
					title=""
					changeModalVisibility={() => setRemoveUserModalVisibility(false)}
					spinning={spinning}
				/>
				<ConfirmationModal
					modalText={
						t("account.activateAccountMessage") + selectedRecord + "? "
					}
					handleFunction={() => activateAccount(selectedUserId)}
					modalVisibility={activateAccountModalVisibility}
					title=""
					changeModalVisibility={() => setActivateAccountModalVisibility(false)}
					spinning={spinning}
				/>

				{selectedRecord && (
					<UpdateMentorInfoModalForm
						userId={selectedUserId}
						isVisible={mentorInfoModalVisibility}
						onClose={() => setMentorInfoModalVisibility(false)}
						onSuccess={handleMentorInfoModalSuccess}
					/>
				)}
			</div>
		</div>
	);
};

export default CompanyUserTable;
